/**
 * useFlowHistory — React binding for the canvas undo/redo history.
 *
 * The logic (what a step is, coalescing, the three-way merge that keeps
 * generation results) lives in src/utils/flowHistory.ts. This hook only:
 *   - feeds every committed {nodes, edges} to the history,
 *   - exposes checkpoint/release to the editor and, via FlowHistoryRef, to
 *     node components,
 *   - applies undo/redo through FUNCTIONAL setNodes/setEdges updaters, so the
 *     transition is re-applied to the live state and a write that landed after
 *     the key press (a generation result) is never overwritten; marks the flow
 *     dirty so autosave persists the restored state,
 *   - runs at most one undo/redo per commit: further presses wait until the
 *     previous one has rendered (otherwise the second would be computed from
 *     the pre-undo graph),
 *   - forgets everything when the open flow changes (`resetKey`),
 *   - owns the keyboard: Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y
 *     redo, on a document listener that ignores keys typed into a text field
 *     (there Ctrl+Z is the browser's own text undo) and yields to modal editors.
 *     The Lightbox stops keys at window capture, so it never reaches here.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Node, Edge } from '@xyflow/react';
import {
  FlowHistory,
  getHistoryShortcut,
  graphsEqual,
  isCanvasHistoryBlocked,
  isEditableTarget,
  transitionEdges,
  transitionNodes,
  type ApplyResult,
  type CheckpointOptions,
  type FlowGraph,
  type HistoryEntry,
} from '../utils/flowHistory';
import { registerFlowHistory, unregisterFlowHistory } from '../services/FlowHistoryRef';

export type HistoryDirection = 'undo' | 'redo';

export interface UseFlowHistoryDeps {
  nodes: Node[];
  edges: Edge[];
  setNodes: Dispatch<SetStateAction<Node[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  markDirty: () => void;
  /** Changing this (e.g. the open flow id) clears the history. */
  resetKey?: unknown;
  isGenerator?: (type: string | undefined) => boolean;
  /** A generation is in flight on this node (lifecycle registry / flow runner). */
  isRunning?: (nodeId: string) => boolean;
  /** Called after an undo/redo actually changed something (e.g. a toast). */
  onApplied?: (direction: HistoryDirection, entry: HistoryEntry) => void;
  /** The next step would remove a node that is generating: nothing was done. */
  onBlocked?: (direction: HistoryDirection, entry: HistoryEntry) => void;
  /** Install the Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y listener (default true). */
  keyboard?: boolean;
  /** Test seam. */
  now?: () => number;
}

export interface FlowHistoryApi {
  checkpoint: (label: string, opts?: CheckpointOptions) => void;
  release: (coalesceKey?: string) => void;
  undo: () => HistoryEntry | null;
  redo: () => HistoryEntry | null;
  clear: () => void;
  /** The last rendered graph (fresh at commit time, unlike a passive-effect ref). */
  peekGraph: () => FlowGraph;
  history: FlowHistory;
}

export function useFlowHistory(deps: UseFlowHistoryDeps): FlowHistoryApi {
  const {
    nodes, edges, setNodes, setEdges, markDirty, resetKey, isGenerator, isRunning, now,
    onApplied, onBlocked, keyboard = true,
  } = deps;

  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;
  const isGeneratorRef = useRef(isGenerator);
  isGeneratorRef.current = isGenerator;
  const running = useCallback((id: string) => !!isRunningRef.current?.(id), []);
  const generator = useCallback((t: string | undefined) => !!isGeneratorRef.current?.(t), []);

  const historyRef = useRef<FlowHistory | null>(null);
  if (!historyRef.current) {
    historyRef.current = new FlowHistory({ isGenerator: generator, now, isRunning: running });
  }
  const history = historyRef.current;

  // Latest rendered graph. Checkpoints are taken from here, i.e. BEFORE the
  // action's own setNodes has been rendered.
  const graphRef = useRef<FlowGraph>({ nodes, edges });
  graphRef.current = { nodes, edges };

  const expireTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Presses that cannot run yet: a held gesture is still landing (the 200ms
  // delete animation, an auto-arrange stagger, a drag), or the previous
  // undo/redo has not rendered.
  const deferredRef = useRef<HistoryDirection[]>([]);
  // An undo/redo was applied and its commit has not rendered yet.
  const awaitingCommitRef = useRef(false);
  const flushScheduledRef = useRef(false);
  const flushDeferredRef = useRef<() => void>(() => {});
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;
  // The "Undone: …" notice for the step being applied. Reported only once the
  // live updaters have run and actually changed something — the decision to
  // undo is taken on the last rendered graph, which a later write can overtake.
  //
  // `ran` matters for the QUEUED path (Ctrl+Z pressed during a held gesture,
  // applied later from a timer): its updaters run in whatever render React
  // schedules next, which can come after the 250ms safety net below or after a
  // commit that does not carry them. Deciding then would read `changed` as
  // false and silently drop the "Undone" toast for an undo that did happen, so
  // the notice is only decided once the updater has actually run.
  //
  // Notices wait in order; a step applied before the previous one's updater
  // ran queues behind it instead of replacing it (that dropped a toast).
  const pendingNoticesRef = useRef<{ direction: HistoryDirection; entry: HistoryEntry; changed: boolean; ran: boolean }[]>([]);
  const reportNotice = useCallback(() => {
    const queue = pendingNoticesRef.current;
    while (queue.length && queue[0].ran) {
      const notice = queue.shift()!;
      if (notice.changed) onAppliedRef.current?.(notice.direction, notice.entry);
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushScheduledRef.current || !deferredRef.current.length) return;
    flushScheduledRef.current = true;
    setTimeout(() => {
      flushScheduledRef.current = false;
      flushDeferredRef.current();
    }, 0);
  }, []);

  // Layout effect so the window closes on the very commit that carries the
  // action, before any passive effect or timer can queue a generation write.
  useLayoutEffect(() => {
    history.observe({ nodes, edges });
    awaitingCommitRef.current = false;
    reportNotice();
    if (deferredRef.current.length && !history.isBusy && !history.hasPending) scheduleFlush();
  }, [history, nodes, edges, scheduleFlush, reportNotice]);

  const clear = useCallback(() => {
    history.clear();
    deferredRef.current = [];
  }, [history]);

  // A different flow was opened. null -> id is NOT a switch: that is the first
  // save of the graph already on screen, and must not wipe its history.
  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    const prev = prevResetKey.current;
    prevResetKey.current = resetKey;
    if (prev === resetKey) return;
    if (prev === null || prev === undefined) return;
    history.clear();
    deferredRef.current = [];
  }, [history, resetKey]);

  // Drop a window that never saw its effect. Re-armed on every checkpoint AND
  // every release, so a held no-op gesture expires after it ends.
  const armExpiry = useCallback(() => {
    if (expireTimer.current) clearTimeout(expireTimer.current);
    expireTimer.current = setTimeout(() => {
      expireTimer.current = null;
      history.expire(graphRef.current);
      flushDeferredRef.current();
    }, 1050);
  }, [history]);

  const checkpoint = useCallback((label: string, opts?: CheckpointOptions) => {
    history.checkpoint(graphRef.current, label, opts);
    armExpiry();
  }, [history, armExpiry]);

  const release = useCallback((coalesceKey?: string) => {
    history.release(coalesceKey);
    armExpiry();
  }, [history, armExpiry]);

  // Apply a step through functional updaters, re-running the transition on
  // whatever the state is when React processes them.
  // An updater that changes nothing returns the current array: no render, and
  // no notice for a step whose effect was already overtaken.
  const apply = useCallback((result: ApplyResult, direction: HistoryDirection) => {
    const { base, target } = result;
    // Settle earlier notices that are decided but not yet reported.
    reportNotice();
    const notice = { direction, entry: result.entry, changed: false, ran: false };
    pendingNoticesRef.current.push(notice);
    let liveNodeIds: Set<string> | null = null;
    setNodes((cur) => {
      notice.ran = true;
      const next = transitionNodes(cur, base, target, generator, running);
      liveNodeIds = new Set(next.map((n) => n.id));
      if (graphsEqual({ nodes: cur, edges: [] }, { nodes: next, edges: [] })) return cur;
      notice.changed = true;
      return next;
    });
    const predicted = new Set(result.graph.nodes.map((n) => n.id));
    setEdges((cur) => {
      const next = transitionEdges(cur, base, target, liveNodeIds ?? predicted);
      if (graphsEqual({ nodes: [], edges: cur }, { nodes: [], edges: next })) return cur;
      notice.changed = true;
      return next;
    });
    awaitingCommitRef.current = true;
    // Safety net: never wait forever if the updaters produced no render.
    setTimeout(() => {
      if (pendingNoticesRef.current.includes(notice)) reportNotice();
      if (!awaitingCommitRef.current) return;
      awaitingCommitRef.current = false;
      flushDeferredRef.current();
    }, 250);
    markDirty();
  }, [setNodes, setEdges, markDirty, generator, running, reportNotice]);
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  /** Run one direction now. Returns false when it was blocked (stop the queue). */
  const run = useCallback((direction: HistoryDirection): { entry: HistoryEntry | null; blocked: boolean } => {
    const result = direction === 'undo' ? history.undo(graphRef.current) : history.redo(graphRef.current);
    if (!result) return { entry: null, blocked: false };
    if (result.blocked) {
      onBlockedRef.current?.(direction, result.entry);
      return { entry: null, blocked: true };
    }
    apply(result, direction);
    return { entry: result.entry, blocked: false };
  }, [history, apply]);

  const undo = useCallback(() => run('undo').entry, [run]);
  const redo = useCallback(() => run('redo').entry, [run]);

  const canRunNow = () => !history.isBusy && !history.hasPending && !awaitingCommitRef.current;

  flushDeferredRef.current = () => {
    if (!deferredRef.current.length || !canRunNow()) return;
    // One direction per commit: apply the first now, the rest after it renders.
    const direction = deferredRef.current.shift()!;
    const { entry, blocked } = run(direction);
    if (blocked) {
      deferredRef.current = [];
      return;
    }
    if (!entry) flushDeferredRef.current();
  };

  useEffect(() => {
    if (!keyboard) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const direction = getHistoryShortcut(event);
      if (!direction) return;
      // Native text undo inside text fields.
      if (isEditableTarget(event.target) || isEditableTarget(document.activeElement)) return;
      if (isCanvasHistoryBlocked()) return;
      event.preventDefault();
      deferredRef.current.push(direction);
      flushDeferredRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [keyboard]);

  useEffect(() => {
    const recorder = { checkpoint, release };
    registerFlowHistory(recorder);
    return () => unregisterFlowHistory(recorder);
  }, [checkpoint, release]);

  useEffect(() => () => {
    if (expireTimer.current) clearTimeout(expireTimer.current);
  }, []);

  const peekGraph = useCallback(() => graphRef.current, []);

  return useMemo(
    () => ({ checkpoint, release, undo, redo, clear, peekGraph, history }),
    [checkpoint, release, undo, redo, clear, peekGraph, history],
  );
}

export default useFlowHistory;
